import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import HelpIcon from "@mui/icons-material/Help";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonIcon from "@mui/icons-material/Person";
import SettingsIcon from "@mui/icons-material/Settings";
import Collapse from "@mui/material/Collapse";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useApi } from "../hooks/useApi";
import { ClientProfile } from "../types/client";
import { Profile } from "../types/contact";

interface Props {}

const Menu: FunctionComponent<Props> = (props: Props): JSX.Element => {
  const { data: profile } = useApi<Profile>("profile");
  const [isPrimaryAdmin, setIsPrimaryAdmin] = useState<boolean>(false);

  useEffect(() => {
    if (profile?.results?.clients && profile.results.clients.length > 0) {
      const clients: ClientProfile[] = profile?.results?.clients || [];
      setIsPrimaryAdmin(clients.some((client) => client.isPrimary));
    }
  }, [profile]);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [openTimesheets, setOpenTimesheets] = useState(true);
  const [openSettings, setOpenSettings] = useState(false);

  const handleClickTimesheets = () => {
    setOpenTimesheets(!openTimesheets);
  };

  const handleClickSettings = () => {
    setOpenSettings(!openSettings);
  };

  const handleOpenTermsOfBusiness = () => {
    const url =
      "https://storage.googleapis.com/mi_bucket_ta/PDF/FileStore/TOB_TEMP.pdf";
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <List sx={{ width: "100%" }}>
      <ListItemButton onClick={handleClickTimesheets}>
        <ListItemIcon>
          <AccessTimeFilledIcon />
        </ListItemIcon>
        <ListItemText primary={t("timesheets.title")}></ListItemText>
        {openTimesheets ? (
          <ExpandLess sx={{ color: "rgba(0, 0, 0, 0.5)" }} />
        ) : (
          <ExpandMore sx={{ color: "rgba(0, 0, 0, 0.5)" }} />
        )}
      </ListItemButton>

      <Collapse in={openTimesheets} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <ListItemButton
            sx={{ pl: 4, pt: "2px", pb: "2px" }}
            onClick={() => navigate("/")}
            selected={location.pathname === "/"}
          >
            <ListItemIcon></ListItemIcon>
            <ListItemText secondary={t("current.title")} />
          </ListItemButton>

          <ListItemButton
            sx={{ pl: 4, pt: "2px", pb: "2px" }}
            onClick={() => navigate("/history")}
            selected={location.pathname === "/history"}
          >
            <ListItemIcon></ListItemIcon>
            <ListItemText secondary={t("history.title")} />
          </ListItemButton>
        </List>
      </Collapse>

      <ListItemButton
        selected={location.pathname === "/contacts"}
        onClick={() => navigate("/contacts")}
      >
        <ListItemIcon>
          <PersonIcon />
        </ListItemIcon>
        <ListItemText
          primary={t("contacts.title")}
          sx={{ marginRight: "20px" }}
        ></ListItemText>
      </ListItemButton>

      <ListItemButton onClick={handleClickSettings}>
        <ListItemIcon>
          <SettingsIcon />
        </ListItemIcon>
        <ListItemText primary={t("settings.title")}></ListItemText>
        {openSettings ? (
          <ExpandLess sx={{ color: "rgba(0, 0, 0, 0.5)" }} />
        ) : (
          <ExpandMore sx={{ color: "rgba(0, 0, 0, 0.5)" }} />
        )}
      </ListItemButton>

      <Collapse in={openSettings} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <ListItemButton
            sx={{ pl: 4, pt: "2px", pb: "2px" }}
            onClick={() => navigate("/settings")}
            selected={
              (location.pathname.split("?")[0] === "/settings" &&
                location.search === "") ||
              (location.pathname.split("?")[0] === "/settings" &&
                location.search === "?tab=1")
            }
          >
            <ListItemIcon></ListItemIcon>
            <ListItemText secondary={t("profilePage.title")} />
          </ListItemButton>

          {isPrimaryAdmin && (
            <ListItemButton
              sx={{ pl: 4, pt: "2px", pb: "2px" }}
              onClick={() => navigate("/settings?tab=2")}
              selected={
                location.pathname.split("?")[0] === "/settings" &&
                location.search === "?tab=2"
              }
            >
              <ListItemIcon></ListItemIcon>
              <ListItemText secondary={t("clientPage.title")} />
            </ListItemButton>
          )}

          {isPrimaryAdmin && (
            <ListItemButton
              sx={{ pl: 4, pt: "2px", pb: "2px" }}
              onClick={() => navigate("/settings?tab=3")}
              selected={
                location.pathname.split("?")[0] === "/settings" &&
                location.search === "?tab=3"
              }
              disabled={!isPrimaryAdmin}
            >
              <ListItemIcon></ListItemIcon>
              <ListItemText secondary={t("adminPage.title")} />
            </ListItemButton>
          )}
        </List>
      </Collapse>

      <ListItemButton
        selected={location.pathname === "/faq"}
        onClick={() => navigate("/faq")}
      >
        <ListItemIcon>
          <HelpIcon />
        </ListItemIcon>
        <ListItemText
          primary={t("FAQ.title")}
          sx={{ marginRight: "20px" }}
        ></ListItemText>
      </ListItemButton>

      <ListItemButton onClick={handleOpenTermsOfBusiness}>
        <ListItemIcon>
          <OpenInNewIcon />
        </ListItemIcon>
        <ListItemText
          primary={t("termsOfBusiness.title")}
          sx={{ marginRight: "20px" }}
        ></ListItemText>
      </ListItemButton>
    </List>
  );
};

export default Menu;
